/*
 *  -----------------------------------------------------------------------
 *    <copyright file="edit-review.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */

import React, { useState, useEffect } from 'react';
import { useMutation } from '@apollo/client/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DialogContent } from '@mui/material';
import FormControl from '@mui/material/FormControl';
import { Checkbox } from '@progress/kendo-react-inputs';
import { DocumentNode } from 'graphql';
import moment from 'moment';
import { useTranslation, useBoolean } from 'hooks';
import { GET_CM_REVIEW_REPORT_DATA, GET_DATASET_REVIEWS, SAVE_DATASET_REVIEW } from 'queries/central-monitoring';
import Constants from 'utils/constants';
import { formatDateUTC_DEPRECATED } from 'utils/convertor';
import { validateFields } from 'utils/validation';
import { SaveButton } from 'views/components/core/buttons';
import CharactersRemaining from 'views/components/core/characters-remaining';
import DatePicker from 'views/components/core/date-picker';
import { CustomDialog, DialogActions, DialogTitle } from 'views/components/core/dialog';
import ModalUpdatedInfo from 'views/components/core/modal-updated-info';
import TextField from 'views/components/core/text-field';

interface IEditReview {
    isReviewDatesReadOnly: boolean;
    open: boolean;
    onClose: (any: any) => any;
    canEdit?: boolean;
    reviewData: any;
    siteId?: string | null;
    dataSetId: string;
    studyReviewInfo?: any;
    studyId: string;
}

const initialState = {
    reviewer: '',
    reviewStartDate: null as any,
    reviewCompleteDate: null as any,
    reviewSummary: '',
};

const EditReview = ({
    isReviewDatesReadOnly,
    open,
    onClose,
    canEdit,
    reviewData,
    siteId,
    dataSetId,
    studyReviewInfo,
    studyId,
}: IEditReview) => {
    const [saveReview] = useMutation(SAVE_DATASET_REVIEW);

    const { t } = useTranslation();
    const reviewed = useBoolean(false);
    const disabledSave = useBoolean(true);

    const [state, setState] = useState({ ...initialState });

    const [errors, setErrors] = useState({} as any);

    useEffect(() => {
        if (reviewData || studyReviewInfo) {
            setState({
                reviewer: reviewData?.reviewer ?? '',
                reviewStartDate: studyReviewInfo
                    ? studyReviewInfo.reviewStartDate
                    : (formatDateUTC_DEPRECATED(reviewData?.reviewStartDate) ?? null),
                reviewCompleteDate: studyReviewInfo
                    ? studyReviewInfo.reviewCompleteDate
                    : (formatDateUTC_DEPRECATED(reviewData?.reviewCompleteDate) ?? null),
                reviewSummary: reviewData?.reviewSummary ?? reviewData?.summary ?? '',
            });
            reviewed.setValue(!!reviewData?.reviewed);
        }
    }, [reviewData, open, studyReviewInfo]);

    const close = (action?: string) => {
        if (action !== 'error') {
            setState({ ...initialState });
            setErrors({});
            disabledSave.setTrue();
        }
        onClose(action);
    };

    const validate = (value?: string, field?: string, length?: number, required?: boolean) => {
        const error = validateFields(value, field, length, required);
        setErrors({
            ...errors,
            ...error,
        });
    };

    const onChange = (name: string) => (event: any) => {
        disabledSave.setFalse();
        let length = 1000;

        if (name === 'reviewer') length = 100;

        setState({
            ...state,
            [name]: event.target.value,
        });
        validate(event.target.value, name, length, false);
    };

    const onDateChange = (name: string) => (event: any) => {
        disabledSave.setFalse();
        setErrors({
            ...errors,
            [name]: null,
        });
        setState({
            ...state,
            [name]: event?.value ? moment(event?.value) : null,
        });
    };

    const submit = () => {
        const refetchQueries: { query: DocumentNode; variables: any }[] = [
            {
                query: GET_DATASET_REVIEWS,
                variables: {
                    studyId,
                    excludeReportReviews: true,
                },
            },
        ];

        if (!isReviewDatesReadOnly)
            refetchQueries.push({
                query: GET_CM_REVIEW_REPORT_DATA,
                variables: {
                    studyId,
                    dataSetId,
                    onlyReviewed: false,
                },
            });
        saveReview({
            variables: {
                dataSetId,
                siteId: siteId ?? Constants.ObjectId.Empty,
                reviewed: reviewed.value,
                summary: state.reviewSummary,
                startDate: !isReviewDatesReadOnly ? formatDateUTC_DEPRECATED(state.reviewStartDate) : null,
                completedDate: !isReviewDatesReadOnly ? formatDateUTC_DEPRECATED(state.reviewCompleteDate) : null,
                reviewer: state.reviewer,
            },
            refetchQueries,
        })
            .then(() => {
                close('success');
            })
            .catch(() => {
                close('error');
            });
    };

    return (
        <CustomDialog onClose={close} fullWidth maxWidth="md" open={open} className="edit-cv-dialog">
            <DialogTitle id="edit-indication-title">
                {t('cm:editReviewModal', { id: reviewData?._itemId ?? reviewData?.recordId })}
                <FontAwesomeIcon
                    icon={['fal', 'times']}
                    className="fa-lg close-icon-cv"
                    onClick={() => {
                        close();
                    }}
                    data-testid="close-dialog"
                />
            </DialogTitle>
            <DialogContent style={{ paddingLeft: 20, paddingRight: 20, paddingTop: 15 }}>
                <Checkbox
                    rounded="none"
                    className="cell-checkbox no-full-width flex-start no-border"
                    checked={reviewed.value}
                    onChange={() => {
                        disabledSave.setFalse();
                        reviewed.setValue(!reviewed.value);
                    }}
                    label={t('cm:reviewed')}
                    data-testid="reviewed"
                    size="medium"
                />
                <br />
                {isReviewDatesReadOnly ? (
                    <div style={{ marginBottom: 15 }}>
                        <p className="description" style={{ marginBottom: 5 }}>
                            <span className="title">{t('cm:reviewStartDate')}: </span>
                            {state.reviewStartDate}
                        </p>
                        <p className="description">
                            <span className="title">{t('cm:reviewCompleteDate')}: </span>
                            {state.reviewCompleteDate}
                        </p>
                    </div>
                ) : null}
                <div className="selects-wrapper not-full" style={{ marginBottom: 5 }}>
                    <TextField
                        label={t('reports:reviewer')}
                        id="edit-review-reviewer"
                        value={state.reviewer}
                        onChange={onChange('reviewer')}
                        size="small"
                        fullWidth
                        placeholder={t('cm:enterReviewer')}
                        helperText={
                            <CharactersRemaining
                                id="edit-review-reviewer-text"
                                length={state.reviewer.length}
                                max={100}
                                error={errors.reviewer}
                            />
                        }
                        variant="outlined"
                        color="secondary"
                        error={!!errors.reviewer}
                    />
                </div>
                {!isReviewDatesReadOnly ? (
                    <div className="selects-wrapper flex-end">
                        <FormControl variant="outlined" className="form-action-type mr">
                            <DatePicker
                                label={t('cm:reviewStartDate')}
                                customId="edit-review-start-date"
                                labelWidth={145}
                                selectedDate={state.reviewStartDate}
                                handleDateChange={onDateChange('reviewStartDate')}
                                error={errors.reviewStartDate}
                                setValid={(value: boolean) => {
                                    if (value === false)
                                        setErrors({
                                            ...errors,
                                            reviewStartDate: t('valueInvalid'),
                                        });
                                }}
                            />
                            {errors.reviewStartDate && <p className="request-date-error">{errors.reviewStartDate}</p>}
                        </FormControl>
                        <FormControl variant="outlined" className="form-action-type">
                            <DatePicker
                                label={t('cm:reviewCompleteDate')}
                                customId="edit-review-complete-date"
                                labelWidth={145}
                                error={errors.reviewCompleteDate}
                                selectedDate={state.reviewCompleteDate}
                                handleDateChange={onDateChange('reviewCompleteDate')}
                                setValid={(value: boolean) => {
                                    if (value === false)
                                        setErrors({
                                            ...errors,
                                            reviewCompleteDate: t('valueInvalid'),
                                        });
                                }}
                            />
                            {errors.reviewCompleteDate && (
                                <p className="request-date-error">{errors.reviewCompleteDate}</p>
                            )}
                        </FormControl>
                    </div>
                ) : null}
                <TextField
                    label={t('cm:reviewSummary')}
                    id="edit-review-summary"
                    multiline
                    rows={5}
                    fullWidth
                    value={state.reviewSummary}
                    onChange={onChange('reviewSummary')}
                    size="small"
                    placeholder={t('cm:enterReviewSummary')}
                    helperText={
                        <CharactersRemaining
                            id="edit-observation-comments"
                            length={state.reviewSummary.length}
                            max={1000}
                            error={errors.reviewSummary}
                        />
                    }
                    variant="outlined"
                    color="secondary"
                    error={!!errors.reviewSummary}
                />
                <DialogActions
                    className={`actions-info-wrapper-edit-cv ${
                        !reviewData?.created && !reviewData?.updated ? 'flex-end' : ''
                    }`}
                    style={{ paddingTop: 15 }}
                >
                    <ModalUpdatedInfo item={reviewData} hideCreated />
                    <SaveButton
                        className="edit-cv-update-btn"
                        id="edit-observation-save"
                        text="update"
                        disabled={
                            Object.keys(errors).filter((item) => errors[item]).length > 0 ||
                            disabledSave.value ||
                            !canEdit
                        }
                        onClick={submit}
                    />
                </DialogActions>
            </DialogContent>
        </CustomDialog>
    );
};

export default EditReview;
